import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganization, useUser } from "@clerk/react";
import { cn } from "@/utils/cn";
import { Button } from "@/components/primitives";
import { Icon } from "@/components/Icon/Icon";
import { MOCK_USER } from "@/services/mock";
import {
  useStarredFlavors,
  useSearchFlavors,
} from "@/hooks/useSidebarFlavors";
import { formatRelative } from "@/utils/formatRelative";
import { useTweaksStore } from "@/stores/tweaksStore";
import type { SidebarFlavor } from "@/utils/types/flavor";
import { SidebarHeader } from "./SidebarHeader";
import { SidebarSearch } from "./SidebarSearch";
import { SidebarFlavorRow } from "./SidebarFlavorRow";
import { SidebarRecentList } from "./SidebarRecentList";

type SidebarProps = {
  activeId?: string;
  onOpenSettings: () => void;
};

function deriveProfile(
  user: ReturnType<typeof useUser>["user"],
  activeOrgName: string | undefined,
) {
  if (!user) {
    return {
      name: MOCK_USER.name,
      org: MOCK_USER.org,
      initials: MOCK_USER.initials,
      email: MOCK_USER.email,
    };
  }

  const name =
    user.fullName ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    user.username ||
    user.primaryEmailAddress?.emailAddress ||
    "Account";

  const initials = (() => {
    if (user.firstName && user.lastName) {
      return (user.firstName[0] + user.lastName[0]).toUpperCase();
    }
    const source =
      user.fullName ||
      user.username ||
      user.primaryEmailAddress?.emailAddress ||
      "";
    const stripped = source.replace(/[^a-zA-Z]/g, "");
    return stripped.slice(0, 2).toUpperCase() || "·";
  })();

  const org = activeOrgName ?? "Personal";

  return {
    name,
    org,
    initials,
    email: user.primaryEmailAddress?.emailAddress || MOCK_USER.email,
  };
}

export function Sidebar({ activeId, onOpenSettings }: SidebarProps) {
  const navigate = useNavigate();
  const { user } = useUser();
  const { organization } = useOrganization();
  const compact = useTweaksStore((s) => s.compactSidebar);
  const setTweak = useTweaksStore((s) => s.setTweak);
  const [query, setQuery] = useState("");

  const profile = useMemo(
    () => deriveProfile(user, organization?.name),
    [user, organization?.name],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const trimmed = query.trim();
  const inSearchMode = trimmed.length > 0;

  const starredQ = useStarredFlavors();
  const searchQ = useSearchFlavors(query);

  const starredItems = starredQ.data?.items ?? [];

  const handleSelect = (f: SidebarFlavor) => {
    navigate(`/flavors/${f.id}`);
  };

  const handleNew = () => navigate("/flavors/new");
  const handleBackToStart = () => navigate("/");

  return (
    <aside className={cn("sidebar", compact ? "collapsed" : "expanded")}>
      <SidebarHeader
        compact={compact}
        onToggleCompact={() => setTweak("compactSidebar", !compact)}
        onBackToStart={handleBackToStart}
      />

      <div className={cn("sidebar-region-new-flavor", compact && "collapsed")}>
        <Button
          variant="primary"
          onClick={handleNew}
          style={{
            width: "100%",
            justifyContent: compact ? "center" : "flex-start",
            padding: compact ? "9px" : "9px 12px",
          }}
        >
          <Icon name="plus" size={15} />
          {!compact && <span>New flavor</span>}
          {!compact && (
            <span
              className="mono"
              style={{
                marginLeft: "auto",
                opacity: 0.6,
                fontSize: 10,
              }}
            >
              ⌘N
            </span>
          )}
        </Button>
      </div>

      <div style={{ height: 1 }} />

      <div className="sidebar-list">
        {!compact && (
          <>
            <SidebarSearch value={query} onChange={setQuery} />
            <div className="sidebar-list-scroll" ref={scrollRef}>
              {inSearchMode ? (
                <SearchResultsSection
                  isLoading={searchQ.isLoading}
                  results={searchQ.data?.items ?? []}
                  query={trimmed}
                  activeId={activeId}
                  onSelect={handleSelect}
                />
              ) : (
                <DefaultSections
                  starred={starredItems}
                  scrollRef={scrollRef}
                  activeId={activeId}
                  onSelect={handleSelect}
                />
              )}
            </div>
          </>
        )}
        {compact && <div style={{ flex: 1 }} />}
      </div>

      <div className={cn("sidebar-profile", compact && "collapsed")}>
        <button
          type="button"
          onClick={onOpenSettings}
          className="sidebar-profile-btn"
        >
          <div className="sidebar-profile-avatar">{profile.initials}</div>
          {!compact && (
            <>
              <div className="sidebar-profile-text">
                <span className="sidebar-profile-name">{profile.name}</span>
                <span className="sidebar-profile-org">{profile.org}</span>
              </div>
              <Icon name="settings" size={14} />
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────

type StarredItem = {
  id: string;
  name: string;
  updatedAt: string;
  starredAt: string;
};

function DefaultSections({
  starred,
  scrollRef,
  activeId,
  onSelect,
}: {
  starred: StarredItem[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
  activeId?: string;
  onSelect: (f: SidebarFlavor) => void;
}) {
  return (
    <>
      {starred.length > 0 && (
        <>
          <div className="sidebar-group-header">
            <span className="label">Starred</span>
            <span
              className="mono"
              style={{ fontSize: 10, color: "var(--ink-4)" }}
            >
              {starred.length}
            </span>
          </div>
          {starred.map((f) => (
            <SidebarFlavorRow
              key={f.id}
              flavor={{
                id: String(f.id),
                name: f.name,
                updated: formatRelative(f.updatedAt),
                starred: true,
              }}
              starred
              active={activeId === String(f.id)}
              onSelect={onSelect}
            />
          ))}
        </>
      )}

      <SidebarRecentList
        scrollRef={scrollRef}
        starredCount={starred.length}
        activeId={activeId}
        onSelect={onSelect}
      />
    </>
  );
}

type SearchResultItem = {
  id: string;
  name: string;
  description: string | null;
  updatedAt: string;
  isStarred: boolean;
};

function SearchResultsSection({
  isLoading,
  results,
  query,
  activeId,
  onSelect,
}: {
  isLoading: boolean;
  results: SearchResultItem[];
  query: string;
  activeId?: string;
  onSelect: (f: SidebarFlavor) => void;
}) {
  if (isLoading && results.length === 0) {
    return <div className="mono sidebar-empty">Searching…</div>;
  }
  if (results.length === 0) {
    return (
      <div className="mono sidebar-empty">No flavors match "{query}"</div>
    );
  }
  return (
    <>
      <div className="sidebar-group-header">
        <span className="label">Results</span>
        <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          {results.length}
        </span>
      </div>
      {results.map((f) => (
        <SidebarFlavorRow
          key={f.id}
          flavor={{
            id: String(f.id),
            name: f.name,
            updated: formatRelative(f.updatedAt),
            starred: f.isStarred,
          }}
          starred={f.isStarred}
          active={activeId === String(f.id)}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}
